interface StoredAuthState {
  state: {
    accessToken: string | null;
    sessionToken: string | null;
    impersonateId: string | null;
    isSessionExpired: boolean | null;
  };
}

async function importKeyFromEnv(
  usage: KeyUsage = 'decrypt',
): Promise<CryptoKey | null> {
  const keyStr = import.meta.env.VITE_REDIRECT_ENCRYPTION_KEY;
  if (!keyStr) return null;
  const raw = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(keyStr),
  );
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [usage]);
}

interface TokenPayload {
  accessToken: string;
  /**
   * The only way to outlive the 24h access token. Absent from older builds of
   * the old dashboard, which is why `storeAuthTokens` treats it as optional
   * rather than required.
   */
  refreshToken?: string | null;
  sessionToken: string | null;
  impersonateId: string | null;
  companyId: string | null;
}

async function decryptTokenParam(
  encoded: string,
): Promise<TokenPayload | null> {
  try {
    const key = await importKeyFromEnv();
    if (!key) return null;
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const combined = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    );
    return JSON.parse(new TextDecoder().decode(decrypted)) as TokenPayload;
  } catch {
    return null;
  }
}

function storeAuthTokens(
  accessToken: string,
  sessionToken: string | null,
  impersonateId: string | null,
  companyId: string | null,
  refreshToken: string | null,
): void {
  const stored = {
    state: {
      accessToken,
      sessionToken: sessionToken ?? null,
      impersonateId: impersonateId ?? null,
      isSessionExpired: false,
      companyId: companyId ?? null,
      // Hardcoded `null` here is what capped a handed-off session at 24h: the
      // access token expired, nothing could renew it, and the user was bounced
      // to login — daily. It must be whatever the old dashboard sent.
      refreshToken,
      sessionStartedAt: Date.now(),
      mePreferencesUpdated: false,
    },
    version: 0,
  };
  localStorage.setItem('retraced:context', JSON.stringify(stored));
  history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
}

export interface SessionTokens {
  accessToken: string;
  refreshToken: string | null;
  /** Ory session token — only SSO still returns one; native sign-in has none. */
  sessionToken?: string | null;
  /** Carried across so the old dashboard resumes the same impersonation. */
  impersonateId?: string | null;
  /** Likewise the effective company, so it does not fall back to the default. */
  companyId?: string | null;
}

/**
 * The session as currently stored, or `null` when there is none. Used when
 * handing the session to the old dashboard — it has to travel in the URL,
 * because localStorage does not cross origins.
 */
export function getStoredSession(): SessionTokens | null {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const { state } = JSON.parse(raw) as {
      state: {
        accessToken?: string | null;
        refreshToken?: string | null;
        sessionToken?: string | null;
        impersonateId?: string | null;
        companyId?: string | null;
      };
    };
    if (!state?.accessToken) return null;
    return {
      accessToken: state.accessToken,
      refreshToken: state.refreshToken ?? null,
      sessionToken: state.sessionToken ?? null,
      impersonateId: state.impersonateId ?? null,
      companyId: state.companyId ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Builds a link into the old dashboard that carries the session with it.
 *
 * localStorage is per-origin, so a session written here is invisible at the old
 * frontend's origin. The old frontend already solves the same problem in the
 * other direction (`buildV2RedirectUrl` → `seedFromHash` below); this is the
 * mirror of it, deliberately using the same AES-GCM scheme, the same
 * `VITE_REDIRECT_ENCRYPTION_KEY`, and the same base64url `#token=` envelope so
 * one decrypt implementation serves both directions.
 *
 * Falls back to the plaintext hash the old frontend also accepts when no key is
 * configured — same trade-off as the existing producer.
 */
export async function buildOldFrontendUrl(
  baseUrl: string,
  path: string,
  tokens: SessionTokens,
): Promise<string> {
  const payload = {
    accessToken: tokens.accessToken,
    sessionToken: tokens.sessionToken ?? null,
    refreshToken: tokens.refreshToken ?? null,
    // Absent for a fresh login — the impersonation picker is exactly where
    // that user is being sent to choose. Present when an established session is
    // moving across, so the old dashboard resumes the same context.
    impersonateId: tokens.impersonateId ?? null,
    companyId: tokens.companyId ?? null,
  };

  try {
    const key = await importKeyFromEnv('encrypt');
    if (!key) return `${baseUrl}${path}${plaintextHash(payload)}`;

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(payload)),
    );
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);
    const token = btoa(String.fromCharCode(...combined))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    return `${baseUrl}${path}#token=${token}`;
  } catch {
    return `${baseUrl}${path}${plaintextHash(payload)}`;
  }
}

function plaintextHash(payload: {
  accessToken: string;
  sessionToken: string | null;
  refreshToken: string | null;
}): string {
  const params = new URLSearchParams({ accessToken: payload.accessToken });
  if (payload.sessionToken) params.set('sessionToken', payload.sessionToken);
  if (payload.refreshToken) params.set('refreshToken', payload.refreshToken);
  return `#${params.toString()}`;
}

/**
 * Writes a freshly issued session to `retraced:context`.
 *
 * The shape has to satisfy **both** apps: v2 reads `accessToken` /
 * `sessionToken` / `companyId` via `getAuthHeaders`, and the old dashboard
 * rehydrates the very same key into its Zustand store. So the record carries
 * every field that store persists — `me` excluded, exactly as the old store
 * does outside development (it refetches the user on load, and a stale copy
 * here would only drift).
 */
export function persistSession(tokens: SessionTokens): void {
  const stored = {
    state: {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? null,
      sessionToken: tokens.sessionToken ?? null,
      sessionStartedAt: Date.now(),
      isSessionExpired: false,
      // A fresh login is never impersonating or scoped to a switched
      // company — those are set later by the flows that start them.
      impersonateId: null,
      companyId: null,
      mePreferencesUpdated: false,
    },
    version: 0,
  };
  localStorage.setItem('retraced:context', JSON.stringify(stored));
}

async function seedFromHash(): Promise<void> {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const params = new URLSearchParams(hash);

    const encryptedToken = params.get('token');
    if (encryptedToken) {
      const payload = await decryptTokenParam(encryptedToken);
      if (payload?.accessToken) {
        storeAuthTokens(
          payload.accessToken,
          payload.sessionToken,
          payload.impersonateId,
          payload.companyId,
          payload.refreshToken ?? null,
        );
      }
      return;
    }

    // Legacy plain-text fallback (remove once old frontend fully migrated)
    const accessToken = params.get('accessToken');
    const sessionToken = params.get('sessionToken');
    const impersonateId = params.get('impersonateId');
    const companyId = params.get('companyId');
    const refreshToken = params.get('refreshToken');
    if (!accessToken) return;
    storeAuthTokens(
      accessToken,
      sessionToken,
      impersonateId,
      companyId,
      refreshToken,
    );
  } catch {
    // ignore
  }
}

async function readStoredAuth(): Promise<StoredAuthState['state'] | null> {
  try {
    await seedFromHash();
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuthState;
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

interface AuthGuardResult {
  allowed: boolean;
  accessToken?: string;
  impersonateId?: string | null;
}

export async function checkAuthGuard(): Promise<AuthGuardResult> {
  const auth = await readStoredAuth();

  // The v2 dashboard is open to every authenticated user — regular company
  // users, demo accounts, and Retraced staff alike, whether impersonating or
  // not. Only an absent or expired session is denied (bounced to the old
  // frontend to log in).
  if (!auth?.accessToken || auth.isSessionExpired) return { allowed: false };

  return {
    allowed: true,
    accessToken: auth.accessToken,
    impersonateId: auth.impersonateId ?? null,
  };
}
